import React, { useEffect } from 'react';
import { Form, Input, DatePicker, Select, Button, Row, Col } from 'antd';
import { Employee } from '../../../types';
import dayjs from 'dayjs';

interface EmployeeFormProps {
    initialValues?: Partial<Employee>;
    onSubmit: (values: any) => void;
    onCancel: () => void;
    loading: boolean;
    serverErrors?: Record<string, string[]>;
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({
    initialValues,
    onSubmit,
    onCancel,
    loading,
    serverErrors = {}
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (Object.keys(serverErrors).length > 0) {
            const errorFields = Object.entries(serverErrors).map(([field, messages]) => ({
                name: field,
                errors: messages,
                validateStatus: 'error'
            }));
            form.setFields(errorFields);
        }
    }, [serverErrors, form]);

    const initialData = initialValues ? {
        ...initialValues,
        date_of_birth: initialValues.date_of_birth ? dayjs(initialValues.date_of_birth) : undefined,
        date_of_hire: initialValues.date_of_hire ? dayjs(initialValues.date_of_hire) : undefined,
    } : undefined;

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            initialValues={initialData}
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{ required: true, message: 'First name is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{ required: true, message: 'Last name is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Email is required' },
                            { type: 'email', message: 'Please enter a valid email' }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="phone_number"
                        label="Phone Number"
                        rules={[{ required: true, message: 'Phone number is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="date_of_birth"
                        label="Date of Birth"
                        rules={[{ required: true, message: 'Date of birth is required' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="gender"
                        label="Gender"
                        rules={[{ required: true, message: 'Gender is required' }]}
                    >
                        <Select>
                            <Select.Option value="Male">Male</Select.Option>
                            <Select.Option value="Female">Female</Select.Option>
                            <Select.Option value="Other">Other</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item
                name="address"
                label="Address"
                rules={[{ required: true, message: 'Address is required' }]}
            >
                <Input.TextArea rows={3} />
            </Form.Item>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="city"
                        label="City"
                        rules={[{ required: true, message: 'City is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="state"
                        label="State"
                        rules={[{ required: true, message: 'State is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="country"
                        label="Country"
                        rules={[{ required: true, message: 'Country is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="postal_code"
                        label="Postal Code"
                        rules={[{ required: true, message: 'Postal code is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="employee_code"
                        label="Employee Code"
                        rules={[{ required: true, message: 'Employee code is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="date_of_hire"
                        label="Date of Hire"
                        rules={[{ required: true, message: 'Date of hire is required' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="job_title"
                        label="Job Title"
                        rules={[{ required: true, message: 'Job title is required' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item
                        name="employment_type"
                        label="Employment Type"
                        rules={[{ required: true, message: 'Employment type is required' }]}
                    >
                        <Select>
                            <Select.Option value="Full-Time">Full-Time</Select.Option>
                            <Select.Option value="Part-Time">Part-Time</Select.Option>
                            <Select.Option value="Contract">Contract</Select.Option>
                            <Select.Option value="Intern">Intern</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item
                name="employment_status"
                label="Employment Status"
                rules={[{ required: true, message: 'Employment status is required' }]}
            >
                <Select>
                    <Select.Option value="Active">Active</Select.Option>
                    <Select.Option value="Terminated">Terminated</Select.Option>
                    <Select.Option value="On Leave">On Leave</Select.Option>
                    <Select.Option value="Resigned">Resigned</Select.Option>
                    <Select.Option value="Retired">Retired</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} style={{ marginRight: 8 }}>
                    {initialValues ? 'Update' : 'Create'}
                </Button>
                <Button onClick={onCancel}>Cancel</Button>
            </Form.Item>
        </Form>
    );
};

export default EmployeeForm;
